import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { pricingPathFor } from '../utils/featurePlans';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3, FileText,
  LogOut, LogIn, Menu, X, Activity, Bot, Bell, Layers, Settings,
  Sigma, Lock, Network, Brain, Briefcase, BookOpen,
} from 'lucide-react';
import { openReportInNewWindow } from '../utils/reportUtils';
import { getRole, getToken, authHeaders } from '../utils';
import { getApiUrl } from '../utils/apiConfig';
import { stepUpSession, passkeyErrorMessage } from '../utils/webauthn';
import { useAuth } from '../context/AuthContext';
import SystemStatusFooter from '../components/SystemStatusFooter';
import GatewayFooter from '../components/GatewayFooter';
import DarkIsland from '../theme/DarkIsland';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  shortcut?: string;
  badge?: string;
  feature?: string; // backend entitlement key; absent = open to every tier
  tier?: TierName; // the plan that unlocks it (shown on the lock badge)
  adminOnly?: boolean;
  locked?: boolean; // caller lacks the entitlement: shown with the tier lock; the route renders the upsell (ProGate)
}

type TierName = 'Standard' | 'Pro' | 'Institutional';

// Access model (owner 2026-09-24): the header is ordered by tier — Free (Analysis) → Standard (Detailed
// Analysis, Risk Kernel, Reports, Generate Institutional Report, Ecosystem Impact, Scenario & Threshold) → Pro
// (Portfolio) → Institutional (contracted capacity; packaging 2026-09-29, internal/auth/entitlements.go); owner-only surfaces (Alerts, Operational, Agent & Governance, Admin)
// follow for the admin role only. Every tier surface is visible to every caller; one the caller's plan does
// not include shows the lock + the tier that unlocks it. The backend enforces the same entitlements.
const NAV_ITEMS: NavItem[] = [
  { to: '/app', icon: BarChart3, label: 'Analysis', shortcut: '1' },
  { to: '/risk-kernel', icon: Sigma, label: 'Risk Kernel', feature: 'risk_kernel', tier: 'Standard' },
  { to: '/reports', icon: FileText, label: 'Reports', shortcut: '3', feature: 'reports', tier: 'Standard' },
  { to: '/portfolio', icon: Layers, label: 'Portfolio', shortcut: '11', feature: 'portfolio', tier: 'Pro' },
  { to: '/alerts', icon: Bell, label: 'Alerts', shortcut: '4', adminOnly: true },
  { to: '/operational', icon: Activity, label: 'Operational', shortcut: '8', adminOnly: true },
  { to: '/agent', icon: Bot, label: 'Agent & Governance', shortcut: '7', adminOnly: true },
];

// ASSET BRIEF (owner 2026-10-07): first in the header; since 2026-10-08 on every plan, Free without an account — the
// same visibility as the Analysis item (shown to every visitor); each plan scoped to its asset universe by the server. Kept out of NAV_ITEMS so the existing items keep their order and
// their Cmd/Ctrl+1..9 shortcuts.
export const BRIEF_NAV: NavItem = { to: '/brief', icon: BookOpen, label: 'Asset Brief' };
const ADMIN_NAV: NavItem = { to: '/admin', icon: Settings, label: 'Admin', shortcut: '15', adminOnly: true };
const FEL_NAV: NavItem = { to: '/forward-evidence', icon: Activity, label: 'Forward Evidence', shortcut: '16', adminOnly: true };
// Institutional Workspace: owner 2026-10-07 — shown to EVERY user so they see it exists; locked (INST badge) unless the
// server says the workspace is open for this caller (admin reviewer, or an entitled organization with a released
// module). The server re-checks every workspace call (plan, organization, object owner).
export const INSTITUTIONAL_NAV: NavItem = { to: '/institutional', icon: Briefcase, label: 'Institutional', feature: 'institutional_workspace', tier: 'Institutional' };

// ONE institutional style for every header control (owner 2026-09-24: no per-type colours) — links and
// asset actions share the same neutral surface, border and text; only the current route is highlighted,
// a control that needs a loaded asset is dimmed until one is loaded.
const HDR_BASE = 'group flex items-center gap-1.5 px-2 py-1.5 rounded-[2px] text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors border whitespace-nowrap';
const HDR_IDLE = 'bg-[color:rgb(var(--c-ink-rgb)/0.02)] text-cmd-textDim border-[color:rgb(var(--c-border-rgb)/0.72)] hover:text-cmd-text hover:bg-[color:rgb(var(--c-ink-rgb)/0.05)]';
const HDR_ACTIVE = 'bg-[color:rgb(var(--c-safe-rgb)/0.12)] text-[color:var(--c-hex-d7dccb)] border-[color:rgb(var(--c-safe-rgb)/0.34)]';
const HDR_DISABLED = 'bg-[color:rgb(var(--c-ink-rgb)/0.02)] text-cmd-textDim border-[color:rgb(var(--c-border-rgb)/0.72)] opacity-45 cursor-not-allowed';
const HDR_MOBILE = 'w-full flex items-center gap-2 px-3 py-2 rounded-[2px] text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors border';

// Header order (tier order): links and asset actions interleaved.
const HEADER_ORDER = ['link:/app', 'action:detailed', 'link:/risk-kernel', 'link:/reports', 'action:report', 'action:ecosystem', 'link:/portfolio', 'action:scenario', 'link:/institutional'];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  // Organisation MFA (owner 2026-09-29): a 403 MFA_* from any protected call raises this prompt.
  const [mfaPrompt, setMfaPrompt] = useState<'' | 'MFA_CHALLENGE_REQUIRED' | 'MFA_ENROLLMENT_REQUIRED'>('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaErr, setMfaErr] = useState('');
  useEffect(() => {
    const on = (e: Event) => setMfaPrompt(((e as CustomEvent).detail?.code || '') as typeof mfaPrompt);
    window.addEventListener('saa-mfa-required', on);
    return () => window.removeEventListener('saa-mfa-required', on);
  }, []);
  const confirmPasskey = useCallback(async () => {
    setMfaBusy(true); setMfaErr('');
    try {
      if (await stepUpSession(getApiUrl)) { window.location.reload(); return; }
      setMfaErr('The session could not be confirmed.');
    } catch (e) {
      setMfaErr(passkeyErrorMessage(e));
    } finally {
      setMfaBusy(false);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { entitlements, tier, logout, loading: authLoading, authenticated, identityState, email, mfaAssurance } = useAuth();
  const role = getRole();
  const free = tier === 'free'; // server-authoritative tier (from /api/auth/me), not localStorage
  // Institutional Workspace (owner 2026-10-03): the entitlement is part of the Institutional plan, but a client sees
  // the item only once the server returns a workspace (a module released + enabled for the organization); the
  // reviewer (admin) always sees it. 404 INSTITUTIONAL_WORKSPACE_NOT_RELEASED ⇒ no item, no badge.
  const instEntitled = entitlements.includes(INSTITUTIONAL_NAV.feature as string);
  const [instOpen, setInstOpen] = useState(false);
  useEffect(() => {
    if (!instEntitled) { setInstOpen(false); return; }
    if (role === 'admin') { setInstOpen(true); return; }
    let alive = true;
    fetch(getApiUrl('/api/institutional/workspace'), { headers: authHeaders() })
      .then((r) => { if (alive) setInstOpen(r.ok); })
      .catch(() => { if (alive) setInstOpen(false); });
    return () => { alive = false; };
  }, [instEntitled, role]);
  const navItems = useMemo(() => {
    const hasFeat = (f: string) => role === 'admin' || entitlements.includes(f);
    return [
      ...NAV_ITEMS.filter((item) => !item.adminOnly || role === 'admin'),
      ...(role === 'admin' ? [FEL_NAV, ADMIN_NAV] : []),
      // Visible to everyone (owner 2026-10-07); open only when the server opened the workspace for this caller.
      INSTITUTIONAL_NAV,
    ].map((item) => ({ ...item, locked: item.to === '/institutional' ? !(role === 'admin' || (instEntitled && instOpen)) : item.feature ? !hasFeat(item.feature) : false }));
  }, [role, entitlements, instEntitled, instOpen]);

  // Only poll notifications once auth has resolved (loading=false) AND the account actually holds the
  // alerts entitlement. Polling during the load-time token refresh raced ahead of the fresh token and
  // 402'd; polling for a genuinely-free account 402s by design. Gating here removes both.
  const canAlerts = !authLoading && authenticated && entitlements.includes('alerts');
  const fetchAlertCount = useCallback(() => {
    if (!canAlerts) { setAlertCount(0); return; }
    const token = getToken();
    if (!token) { setAlertCount(0); return; }
    fetch(getApiUrl('/api/alerts/notifications?limit=50'), { headers: authHeaders() })
      .then((res) => { if (!res.ok) throw new Error(String(res.status)); return res.json(); })
      .then((json) => {
        if (json.success) setAlertCount(typeof json.unread_count === 'number' ? json.unread_count : (Array.isArray(json.data) ? json.data.length : 0));
      })
      .catch(() => { setAlertCount(0); });
  }, [canAlerts]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Asset actions (Analysis page): Ecosystem Impact + Scenario & Threshold Analysis open the selected asset in
  // a new tab. The asset is the one whose data the Analysis page has LOADED (sessionStorage + 'saa-asset-loaded').
  const [asset, setAsset] = useState<string>(() => {
    try { return (sessionStorage.getItem('saa_loaded_asset') || '').toLowerCase(); } catch { return ''; }
  });
  useEffect(() => {
    const read = () => {
      try { setAsset((sessionStorage.getItem('saa_loaded_asset') || '').toLowerCase()); } catch { /* ignore */ }
    };
    const onAsset = (e: Event) => setAsset(String((e as CustomEvent).detail || ''));
    window.addEventListener('saa-asset-loaded', onAsset);
    read(); // the Analysis page may have published its asset before this listener existed (child effects run first)
    return () => window.removeEventListener('saa-asset-loaded', onAsset);
  }, [location.pathname]);
  // Always present in the header row (every page); ACTIVE only once the Analysis page has LOADED an asset,
  // otherwise visible but dim and disabled.
  const assetActions: { key: string; label: string; icon: React.ElementType; feature: string; tier: TierName; href: string; title: string }[] = [
    { key: 'detailed', label: 'View Detailed Analysis', icon: Brain, feature: 'detailed_analysis', tier: 'Standard', href: asset ? `/analysis/${encodeURIComponent(asset)}` : '', title: 'Detailed Analysis (Dossier View) for the loaded asset (opens in a new window)' },
    { key: 'report', label: 'Generate Institutional Report', icon: FileText, feature: 'reports', tier: 'Standard', href: asset ? `report:${asset}` : '', title: 'Generate the Institutional Report for the loaded asset (opens in a new tab)' },
    { key: 'ecosystem', label: 'Ecosystem Impact', icon: Network, feature: 'ecosystem', tier: 'Standard', href: asset ? `/ecosystem/v2/${asset}` : '', title: 'Risk transmission map for the selected asset (opens in a new tab)' },
    { key: 'scenario', label: 'Scenario & Threshold', icon: Activity, feature: 'scenario', tier: 'Standard', href: asset ? `/risk-analytics/${asset}` : '', title: 'Scenario & threshold analysis · Risk Desk (opens in a new tab) · descriptive, not advice' },
  ];
  const actionAllowed = (feature: string) => role === 'admin' || entitlements.includes(feature);
  const openAssetAction = async (a: { feature: string; href: string }) => {
    if (!a.href) return; // disabled until an asset has loaded
    if (!actionAllowed(a.feature)) { navigate(pricingPathFor(a.feature)); return; }
    if (a.href.startsWith('report:')) {
      // Async render (504-safe): opens the tab synchronously, then navigates it to the generating page.
      await openReportInNewWindow({ symbol: a.href.slice(7), language: 'en', includeStoredReportId: false });
      return;
    }
    window.open(a.href, '_blank', 'noopener');
  };
  const tierBadge = (tier?: TierName) => (
    <span className="flex items-center gap-0.5 text-[8px] px-1 py-0.5 rounded-[2px] border border-[color:rgb(var(--c-active-rgb)/0.28)] text-cmd-textDim font-semibold leading-none tracking-[0.1em]">
      <Lock size={8} /><span className="hidden min-[1800px]:inline">{tier === 'Institutional' ? 'INST' : (tier || 'PRO').toUpperCase()}</span>
    </span>
  );
  const renderAction = (a: typeof assetActions[number], where: 'desktop' | 'mobile') => {
    const Icon = a.icon;
    const allowed = actionAllowed(a.feature);
    return (
      <button
        key={`${a.key}-${where}`}
        onClick={() => openAssetAction(a)}
        disabled={!asset}
        aria-disabled={!asset}
        title={!asset ? 'Load an asset on the Analysis page first' : !allowed ? `Included from the ${a.tier} plan` : a.title}
        className={where === 'desktop'
          ? `${HDR_BASE} ${asset ? `${HDR_IDLE} cursor-pointer` : HDR_DISABLED}`
          : `${HDR_MOBILE} ${asset ? HDR_IDLE : HDR_DISABLED}`}
      >
        <span className={`shrink-0 ${where === 'desktop' ? 'hidden min-[1800px]:inline' : ''}`}><Icon size={14} /></span>
        <span className={`font-medium whitespace-nowrap ${where === 'mobile' ? 'flex-1 text-left' : ''}`}>{a.label}</span>
        {asset && allowed && <span className="text-[9px] font-mono text-cmd-textMuted tracking-[0.06em]">{asset.toUpperCase()}</span>}
        {!allowed && tierBadge(a.tier)}
      </button>
    );
  };
  // The header in tier order: links and asset actions interleaved (HEADER_ORDER), then owner-only links.
  const headerEntries = [
    { kind: 'link' as const, item: BRIEF_NAV }, // every visitor, as Analysis (Free: no account required)
    ...HEADER_ORDER.map((k) => {
      if (k.startsWith('link:')) {
        const item = navItems.find((n) => n.to === k.slice(5));
        return item ? { kind: 'link' as const, item } : null;
      }
      const a = assetActions.find((x) => x.key === k.slice(7));
      return a ? { kind: 'action' as const, a } : null;
    }),
    ...navItems.filter((n) => !HEADER_ORDER.includes(`link:${n.to}`)).map((item) => ({ kind: 'link' as const, item })),
  ].filter(Boolean) as ({ kind: 'link'; item: NavItem } | { kind: 'action'; a: typeof assetActions[number] })[];

  useEffect(() => {
    fetchAlertCount();
    const t = setInterval(fetchAlertCount, 20000);
    const onAlertsUpdated = () => fetchAlertCount();
    window.addEventListener('alerts-updated', onAlertsUpdated);
    return () => { clearInterval(t); window.removeEventListener('alerts-updated', onAlertsUpdated); };
  }, [fetchAlertCount]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const idx = parseInt(e.key, 10) - 1;
        if (navItems[idx]) {
          navigate(navItems[idx].to);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, navItems]);

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-cmd-bg">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Top navigation bar — stays on the dark tokens in every theme (owner 2026-10-05: header and footer
          stay black as in the dark theme). */}
      <DarkIsland>
      <header className="print-hide shrink-0 border-b border-[color:rgb(var(--c-border-rgb)/0.95)] bg-[color:rgb(var(--c-surface-rgb)/0.98)] backdrop-blur-[2px]">
        <div className="flex items-center min-h-[60px] pl-2 pr-3 md:pl-4 md:pr-4 lg:pl-5 lg:pr-5 gap-3">
          {/* Brand logo */}
          <NavLink to="/" end className="mr-4 shrink-0 flex items-center border-r border-[color:rgb(var(--c-border-rgb)/0.95)] pr-4" aria-label="Scientific Analytics Alliance — entry page">
            <img
              src={`${process.env.PUBLIC_URL}/saa-logo.svg`}
              alt="Scientific Analytics Alliance"
              className="h-10 md:h-11"
              style={{ minWidth: 120 }}
            />
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1 flex-wrap" role="navigation" aria-label="Main navigation">
            {headerEntries.map((e) => {
              if (e.kind === 'action') return renderAction(e.a, 'desktop');
              const item = e.item;
              const Icon = item.icon;
              const isActive = location.pathname === item.to || (item.to === '/institutional' && location.pathname.startsWith('/institutional/')) || (item.to === '/brief' && location.pathname.startsWith('/brief/')) ||
                (item.to === '/' && location.pathname === '/dashboard');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`${HDR_BASE} relative ${isActive ? HDR_ACTIVE : HDR_IDLE}`}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.locked ? `Included from the ${item.tier} plan` : undefined}
                >
                  <span className={`relative shrink-0 ${item.to === '/alerts' ? '' : 'hidden min-[1800px]:inline'}`}>
                    <Icon size={14} />
                    {item.to === '/alerts' && alertCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-[2px] bg-[color:rgb(var(--c-alert-red-rgb)/0.95)] text-[9px] font-bold text-white leading-none font-mono">
                        {alertCount > 99 ? '99+' : alertCount}
                      </span>
                    )}
                  </span>
                  <span className="font-medium whitespace-nowrap">{item.label}</span>
                  {item.locked && tierBadge(item.tier)}
                  {item.badge && (
                    <span className="text-[8px] px-1 py-0.5 rounded-[2px] bg-[color:rgb(var(--c-alert-amber-rgb)/0.1)] border border-[color:rgb(var(--c-alert-amber-rgb)/0.18)] text-cmd-warning font-bold leading-none font-mono">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Right side — plans / account / sign in / sign out. Signed-in state comes from the session
              (authenticated), not the tier: a signed-in Free account is still signed in. */}
          <div className="hidden md:flex items-center gap-2 ml-auto shrink-0">
            {free && (
              <NavLink to="/pricing" className="text-[10px] uppercase tracking-[0.14em] text-cmd-textDim hover:text-cmd-text transition-colors px-2 py-1.5">Plans</NavLink>
            )}
            {!authenticated ? (
              <>
                <NavLink
                  to="/login"
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-cmd-textDim hover:text-cmd-text transition-colors border border-[color:rgb(var(--c-border-rgb)/0.8)] px-2.5 py-1.5 rounded-[2px]"
                >
                  <LogIn size={12} /><span>Sign In</span>
                </NavLink>
              </>
            ) : (
              <>
                <NavLink to="/account" className="text-[10px] uppercase tracking-[0.14em] text-cmd-textDim hover:text-cmd-text transition-colors px-2 py-1.5">Account</NavLink>
                <button
                  onClick={logout}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-cmd-textDim hover:text-cmd-text transition-colors border border-[color:rgb(var(--c-border-rgb)/0.8)] px-2.5 py-1.5 rounded-[2px]"
                >
                  <LogOut size={12} /><span>Sign Out</span>
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden ml-auto text-cmd-textMuted hover:text-cmd-text transition-colors border border-[color:rgb(var(--c-border-rgb)/0.8)] px-2 py-1.5 rounded-[2px] bg-[color:rgb(var(--c-ink-rgb)/0.02)]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <nav className="md:hidden border-t border-[color:rgb(var(--c-border-rgb)/0.95)] bg-[color:rgb(var(--c-surface-rgb)/0.98)] px-3 py-3 space-y-1" role="navigation" aria-label="Mobile navigation">
            {headerEntries.map((e) => {
              if (e.kind === 'action') return renderAction(e.a, 'mobile');
              const item = e.item;
              const Icon = item.icon;
              const isActive = location.pathname === item.to || (item.to === '/institutional' && location.pathname.startsWith('/institutional/')) || (item.to === '/brief' && location.pathname.startsWith('/brief/')) ||
                (item.to === '/' && location.pathname === '/dashboard');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`${HDR_MOBILE} ${isActive ? HDR_ACTIVE : HDR_IDLE}`}
                >
                  <span className="relative shrink-0">
                    <Icon size={14} />
                    {item.to === '/alerts' && alertCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-[2px] bg-[color:rgb(var(--c-alert-red-rgb)/0.95)] text-[10px] font-bold text-white leading-none font-mono">
                        {alertCount > 99 ? '99+' : alertCount}
                      </span>
                    )}
                  </span>
                  <span className="flex-1 font-medium">{item.label}</span>
                  {item.locked && tierBadge(item.tier)}
                </NavLink>
              );
            })}
          </nav>
        )}
      </header>
      </DarkIsland>

      {/* Main content */}
      {/* Identity stage B: account_created ≠ identity_verified — a quiet, persistent prompt until the address is proven. */}
      {/* Stage C: an account that requires MFA but has no passkey is asked to add one (admin / organization). */}
      {mfaPrompt === 'MFA_CHALLENGE_REQUIRED' && (
        <div role="alert" className="px-4 md:px-10 lg:px-20 py-2 text-[12px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1" style={{ background: 'rgb(var(--c-amber-rgb) / 0.1)', borderBottom: '1px solid rgb(var(--c-amber-rgb) / 0.35)', color: 'var(--c-amber)' }}>
          <span>Your organisation requires a passkey-confirmed session for this data.</span>
          <button type="button" onClick={confirmPasskey} disabled={mfaBusy} className="underline hover:text-[color:var(--c-gold-pale)] disabled:opacity-60">{mfaBusy ? 'Waiting for passkey…' : 'Confirm with passkey →'}</button>
          {mfaErr && <span className="text-red-300/90">{mfaErr}</span>}
        </div>
      )}
      {!authLoading && authenticated && (mfaAssurance === 'ENROLLMENT_REQUIRED' || mfaPrompt === 'MFA_ENROLLMENT_REQUIRED') && location.pathname !== '/account/security' && (
        <div role="status" className="print-hide px-4 md:px-10 lg:px-20 py-1.5 text-[11.5px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1" style={{ background: 'rgb(var(--c-amber-rgb) / 0.08)', borderBottom: '1px solid rgb(var(--c-amber-rgb) / 0.28)', color: 'var(--c-amber)' }}>
          <span>Your account requires a passkey. Add one to keep access to protected surfaces.</span>
          <Link to="/account/security" className="underline hover:text-[color:var(--c-gold-pale)]">Add passkey →</Link>
        </div>
      )}
      {!authLoading && authenticated && identityState === 'ACCOUNT_CREATED' && location.pathname !== '/account/security' && (
        <div role="status" className="print-hide px-4 md:px-10 lg:px-20 py-1.5 text-[11.5px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1" style={{ background: 'rgb(var(--c-gold-rgb) / 0.06)', borderBottom: '1px solid rgb(var(--c-gold-rgb) / 0.2)', color: 'var(--c-hex-c9b27a)' }}>
          <span>Verify your email to complete your account identity{email ? ` — a link was sent to ${email}` : ''}.</span>
          <Link to="/account/security" className="underline hover:text-[color:var(--c-gold-pale)]">Resend or manage →</Link>
        </div>
      )}
      <main id="main-content" className="flex-1 overflow-y-auto bg-cmd-bg" role="main" tabIndex={-1}>
        {children}
      </main>

      {/* Unified platform footer — the entry-gateway rail, identical across every terminal and
          access-gated page (admin / pro / free / account). */}
      <DarkIsland><GatewayFooter /></DarkIsland>

      {/* OP-4: single client-facing system-status line (honest aggregate, no internals). */}
      <div className="print-hide contents"><DarkIsland><SystemStatusFooter alertCount={alertCount} /></DarkIsland></div>
    </div>
  );
}
